import { X } from "lucide-react";
import { Toast as ToastPrimitive } from "radix-ui";
import { useEffect, useState } from "react";
import { POPUP_SURFACE } from "../../lib/surfaces";
import { TONES, type Tone } from "../../lib/tones";
import { cn } from "../../lib/utils";

export type ToastTone = Extract<Tone, "success" | "error" | "info" | "warning">;

export type ToastMessage = {
	title: string;
	description?: string;
	tone: ToastTone;
	/** A button acting on the toast (it closes the toast). */
	action?: { label: string; onSelect: () => void };
	/** How long it stays, in milliseconds (default 5 s; `Infinity` until dismissed). */
	duration?: number;
};

const TOAST_EVENT = "crowdex:toast";

export function showToast(message: ToastMessage): void {
	if (typeof window === "undefined") return;
	window.dispatchEvent(
		new CustomEvent<ToastMessage>(TOAST_EVENT, { detail: message }),
	);
}

export const toast = {
	success(title: string, description?: string): void {
		showToast({ title, description, tone: "success" });
	},
	error(title: string, description?: string): void {
		showToast({ title, description, tone: "error" });
	},
	info(title: string, description?: string): void {
		showToast({ title, description, tone: "info" });
	},
};

type ToastItem = ToastMessage & { id: number };

let nextToastId = 0;

export function AppToaster() {
	const [items, setItems] = useState<ToastItem[]>([]);

	useEffect(() => {
		function onToast(event: Event): void {
			const message = (event as CustomEvent<ToastMessage>).detail;
			const item = { ...message, id: ++nextToastId };
			setItems((current) => [...current.slice(-3), item]);
		}

		window.addEventListener(TOAST_EVENT, onToast);
		return () => window.removeEventListener(TOAST_EVENT, onToast);
	}, []);

	return (
		<ToastPrimitive.Provider duration={5000} swipeDirection="right">
			{items.map((item) => {
				const { icon: Icon, mark } = TONES[item.tone];
				return (
					<ToastPrimitive.Root
                        data-slot="toast"
						{...(item.duration === undefined ? {} : { duration: item.duration })}
						className={cn(
							POPUP_SURFACE,
							"grid grid-cols-[auto_1fr_auto] items-start gap-x-3 rounded-lg py-3 pr-3 pl-3.5 outline-none duration-overlay data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-right-4 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-2",
						)}
						key={item.id}
						onOpenChange={(open) => {
							if (!open) {
								setItems((current) =>
									current.filter(({ id }) => id !== item.id),
								);
							}
						}}
					>
						<Icon aria-hidden="true" className={cn("mt-1 size-4 shrink-0", mark)} />
						<div className="min-w-0">
							<ToastPrimitive.Title className="type-ui pt-1 font-medium">
								{item.title}
							</ToastPrimitive.Title>
							{item.description !== undefined && (
								<ToastPrimitive.Description className="type-caption mt-0.5 text-muted-foreground">
									{item.description}
								</ToastPrimitive.Description>
							)}
							{item.action !== undefined && (
								<ToastPrimitive.Action
									altText={item.action.label}
									className="mt-2 inline-flex h-control-sm items-center rounded-control border border-hairline-strong px-2.5 font-medium type-caption transition-colors hover:bg-accent"
									onClick={item.action.onSelect}
								>
									{item.action.label}
								</ToastPrimitive.Action>
							)}
						</div>
						<ToastPrimitive.Close
							aria-label="Dismiss notification"
							className="-mr-1 -mt-0.5 flex size-control-sm items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
						>
							<X className="size-3.5" />
						</ToastPrimitive.Close>
					</ToastPrimitive.Root>
				);
			})}
			<ToastPrimitive.Viewport data-slot="toast-viewport" className="fixed right-3 bottom-4 z-toast flex w-[min(24rem,calc(100vw-2rem))] max-w-full flex-col gap-2 outline-none" />
		</ToastPrimitive.Provider>
	);
}
